import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { formatInr } from '../lib/formatInr';
import { displayAmount } from '../lib/bookingAmount';
import { BagInsuranceField } from './BagInsuranceField';
import { X, ShieldCheck, Send, RefreshCcw, Upload, Save, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * OTP-gated luggage-manifest edit flow for Agent + Partner.
 *
 * Stages:
 *   idle    → agent clicks REQUEST OTP → backend sends WhatsApp OTP to customer
 *   otp     → customer reads OTP to agent → agent enters 6 digits → verified
 *   editing → agent can (a) add photos, (b) edit bag weight/type/marks
 *   saving  → PATCH applied, single-shot token invalidated by backend
 *   done    → success confirmation
 */
export const EditAuthorisationModal = ({ role, roleCode, booking, onClose, onSaved }) => {
  const [stage, setStage] = useState('idle');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // OTP state
  const [otpInfo, setOtpInfo] = useState(null);
  const [otp, setOtp] = useState('');
  const [authToken, setAuthToken] = useState('');

  // Editable copy of luggage_details (deep-cloned on stage transition)
  const [bags, setBags] = useState([]);
  const [addPhotos, setAddPhotos] = useState([]);  // base64 strings
  const [photoTargetBagIdx, setPhotoTargetBagIdx] = useState(0);
  const [editNotes, setEditNotes] = useState('');
  const [pricing, setPricing] = useState(null);

  const bookingId = booking?.booking_id;
  const roleUrl = role === 'agent' ? 'agent' : 'partner';

  // Prettify booking summary
  const summaryRows = React.useMemo(() => {
    if (!booking) return [];
    const ld = booking.luggage_details || {};
    const totalWeight = (ld.bags || []).reduce((s, b) => s + (parseFloat(b.weight) || 0), 0);
    return [
      { label: 'BAGGAGE TAG', value: booking.baggage_tag || booking.booking_id || '—' },
      { label: 'STATUS', value: booking.tracking_status || '—' },
      { label: 'CUSTOMER', value: booking.customer_name || '—' },
      { label: 'PHONE', value: booking.customer_phone || '—' },
      { label: 'PICKUP', value: booking.pickup_location || '—' },
      { label: 'DELIVERY', value: booking.delivery_location || '—' },
      { label: 'SCHEDULED PICKUP', value: booking.scheduled_pickup_date
          ? `${booking.scheduled_pickup_date}${booking.scheduled_pickup_time ? ' · ' + booking.scheduled_pickup_time : ''}`
          : '—' },
      { label: 'BAGS ON FILE', value: `${(ld.bags || []).length} · ${totalWeight.toFixed(1)} kg total` },
      { label: 'AMOUNT', value: booking.amount ? formatInr(displayAmount(booking)) : '—' },
    ];
  }, [booking]);

  // Escape-key dismiss
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const requestOtp = async () => {
    setError(''); setBusy(true);
    try {
      const res = await axios.post(`${API}/${roleUrl}/${roleCode}/bookings/${bookingId}/edit-otp/send`);
      setOtpInfo(res.data);
      setStage('otp');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send OTP');
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) { setError('Enter the 6-digit OTP'); return; }
    setError(''); setBusy(true);
    try {
      const res = await axios.post(`${API}/${roleUrl}/${roleCode}/bookings/${bookingId}/edit-otp/verify`, { otp });
      setAuthToken(res.data.authorization_token);
      // Prime the edit form from the current manifest
      const existingBags = Array.isArray(booking?.luggage_details?.bags) ? booking.luggage_details.bags : [];
      setBags(existingBags.map((b) => ({ ...b })));
      setStage('editing');
    } catch (err) {
      setError(err?.response?.data?.detail || 'OTP verification failed');
    } finally { setBusy(false); }
  };

  const handlePhotoFiles = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    const readers = arr.map((f) => new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(f);
    }));
    Promise.all(readers).then((results) => {
      setAddPhotos((prev) => [...prev, ...results]);
    });
  };

  const updateBagField = (idx, field, value) => {
    setBags((prev) => prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)));
  };

  const applySave = async () => {
    const missing = (bags || []).findIndex((b) => !String(b.color || '').trim());
    if (missing >= 0) { setError(`Bag #${missing + 1}: please enter the colour`); return; }
    const badIns = (bags || []).findIndex((b) => b.insurance_opted && !((parseFloat(b.declared_value) || 0) > 0 && parseFloat(b.declared_value) <= 10000));
    if (badIns >= 0) { setError(`Bag #${badIns + 1}: enter a declared value between ₹1 and ₹10,000 for insurance`); return; }
    setError(''); setBusy(true);
    try {
      const payload = {
        authorization_token: authToken,
        edit_notes: editNotes,
      };
      if (bags && bags.length > 0) {
        payload.bags = bags.map((b) => ({
          ...b,
          weight: parseFloat(b.weight) || 0,
          // Preserve nested fields the summary panel showed but the form
          // doesn't edit — dimensions, brand, declared_value, existing images.
          dimensions: b.dimensions || '50x30x20 cm',
          declared_value: b.insurance_opted ? (parseFloat(b.declared_value) || 0) : 0,
          insurance_opted: !!b.insurance_opted,
          images: Array.isArray(b.images) ? b.images : [],
        }));
      }
      if (addPhotos.length > 0) {
        payload.add_photos = addPhotos;
        payload.add_photos_bag_index = photoTargetBagIdx;
      }
      const res = await axios.patch(`${API}/${roleUrl}/${roleCode}/bookings/${bookingId}/edit`, payload);
      setPricing(res.data?.pricing || null);
      setStage('done');
      if (onSaved) onSaved(res.data?.booking);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const tag = booking?.baggage_tag || booking?.booking_id;
  const customerName = booking?.customer_name || 'Customer';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-start sm:items-center justify-center p-4 z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="edit-auth-modal-overlay"
    >
      <div className="card-brutalist bg-white max-w-2xl w-full max-h-[90vh] flex flex-col" data-testid="edit-auth-modal">
        <div className="flex items-center justify-between p-5 pb-3 border-b-2 border-black bg-white sticky top-0 rounded-t-[inherit]">
          <div>
            <h2 className="text-lg font-black uppercase flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              <ShieldCheck size={18} /> EDIT LUGGAGE MANIFEST
            </h2>
            <p className="text-xs font-mono uppercase text-gray-600 mt-0.5">
              {tag} · {customerName} · Customer authorisation required
            </p>
          </div>
          <button onClick={onClose} data-testid="edit-auth-close" aria-label="Close" className="p-2 -m-2 hover:text-orange-600"><X size={22}/></button>
        </div>

        <div className="overflow-y-auto p-5 pt-3 flex-1">
          {/* Read-only booking snapshot — always visible so the agent knows
              exactly which booking (and what's currently on file) before
              they change anything. */}
          <div className="mb-4 border-2 border-black bg-[#FFF7E6] p-3" data-testid="edit-auth-booking-summary">
            <div className="text-[10px] font-mono uppercase text-gray-600 mb-2">BOOKING ON FILE</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2 text-xs font-mono">
              {summaryRows.map((row) => (
                <div key={row.label}>
                  <div className="text-[9px] uppercase text-gray-500">{row.label}</div>
                  <div className="font-bold text-[#1A1A1A] break-words">{row.value}</div>
                </div>
              ))}
            </div>
            {/* Current photos strip */}
            {(booking?.luggage_details?.bags || []).some((b) => (b.images || []).length > 0) && (
              <div className="mt-3 pt-3 border-t border-black/20">
                <div className="text-[10px] font-mono uppercase text-gray-600 mb-1">CURRENT PHOTOS</div>
                <div className="flex flex-wrap gap-2">
                  {(booking.luggage_details.bags || []).map((b, bi) => (
                    (b.images || []).map((src, ii) => (
                      <div key={`${bi}-${ii}`} className="relative">
                        <img src={src} alt={`bag-${bi + 1}-${ii + 1}`} className="w-16 h-16 object-cover border-2 border-black" />
                        <div className="absolute -top-1.5 -left-1.5 bg-white border-2 border-black text-[8px] px-1 font-mono">B{bi + 1}</div>
                      </div>
                    ))
                  ))}
                </div>
              </div>
            )}
          </div>

          {error && (
            <div data-testid="edit-auth-error" className="mb-3 p-2 border-2 border-red-600 bg-red-50 text-red-700 text-xs font-mono flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}

          {stage === 'idle' && (
            <div data-testid="edit-auth-stage-idle">
              <p className="text-sm font-mono leading-relaxed mb-4">
                To edit this booking's photos, weight or bag details we need the
                customer's consent. Clicking below sends a 6-digit OTP to the
                customer's WhatsApp on their <b>registered mobile</b> — ask them to
                read it out to you.
              </p>
              <button
                data-testid="edit-auth-send-otp"
                disabled={busy}
                onClick={requestOtp}
                className="btn-primary w-full py-3 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Send size={16} /> SEND OTP VIA WHATSAPP
              </button>
            </div>
          )}

          {stage === 'otp' && (
            <div data-testid="edit-auth-stage-otp">
              <div className="mb-3 p-2 border-2 border-black bg-green-50 text-xs font-mono">
                OTP sent to <b>{otpInfo?.target_masked || 'customer'}</b>.
                Valid {Math.round((otpInfo?.expires_in || 600) / 60)} minutes.
                {otpInfo?.otp_debug && (
                  <div className="mt-1 text-orange-700">DEV OTP (mock channel): {otpInfo.otp_debug}</div>
                )}
              </div>
              <label className="text-xs font-mono uppercase text-gray-600">Enter OTP recited by customer</label>
              <input
                type="text"
                inputMode="numeric"
                autoFocus
                data-testid="edit-auth-otp-input"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full mt-1 mb-3 border-2 border-black px-3 py-3 font-mono text-lg tracking-[0.4em] text-center"
                placeholder="••••••"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setOtp(''); setStage('idle'); }}
                  className="btn-secondary px-4 py-2 text-xs"
                >
                  <RefreshCcw size={12} className="inline mr-1" /> RESEND
                </button>
                <button
                  data-testid="edit-auth-verify-otp"
                  disabled={busy || otp.length !== 6}
                  onClick={verifyOtp}
                  className="btn-primary flex-1 py-2 text-sm disabled:opacity-60"
                >
                  {busy ? 'VERIFYING…' : 'VERIFY & OPEN EDIT WINDOW'}
                </button>
              </div>
            </div>
          )}

          {stage === 'editing' && (
            <div data-testid="edit-auth-stage-editing" className="space-y-4">
              <div className="p-2 border-2 border-black bg-green-50 text-xs font-mono">
                Customer authorised. This is a <b>one-shot</b> edit window — after
                you save, any further changes will require a fresh OTP.
              </div>

              {/* Bag rows */}
              <div>
                <div className="text-xs font-mono uppercase text-gray-600 mb-1">BAGS ({bags.length})</div>
                <div className="space-y-3">
                  {bags.map((b, idx) => (
                    <div key={idx} className="border-2 border-black p-3 bg-gray-50" data-testid={`edit-auth-bag-row-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-mono uppercase">Bag #{idx + 1}</div>
                        {bags.length > 1 && (
                          <button
                            type="button"
                            data-testid={`edit-auth-remove-bag-${idx}`}
                            onClick={() => {
                              setBags((prev) => prev.filter((_, i) => i !== idx));
                              setPhotoTargetBagIdx(0);
                            }}
                            className="text-[10px] font-mono uppercase border border-red-600 text-red-700 px-2 py-0.5 hover:bg-red-50"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div>
                          <label className="text-[10px] font-mono uppercase text-gray-500">Type</label>
                          <select
                            value={b.bag_type || 'suitcase'}
                            onChange={(e) => updateBagField(idx, 'bag_type', e.target.value)}
                            className="w-full border-2 border-black px-2 py-1 text-sm font-mono bg-white"
                            data-testid={`edit-auth-bag-type-${idx}`}
                          >
                            {['suitcase','trolley','backpack','duffle','box','bag','other'].map((t) => (
                              <option key={t} value={t}>{t.toUpperCase()}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-mono uppercase text-gray-500">Weight (kg)</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={b.weight ?? ''}
                            onChange={(e) => updateBagField(idx, 'weight', e.target.value)}
                            className="w-full border-2 border-black px-2 py-1 text-sm font-mono"
                            data-testid={`edit-auth-bag-weight-${idx}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono uppercase text-gray-500">Color</label>
                          <input
                            type="text"
                            value={b.color || ''}
                            onChange={(e) => updateBagField(idx, 'color', e.target.value)}
                            className="w-full border-2 border-black px-2 py-1 text-sm font-mono"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono uppercase text-gray-500">Material</label>
                          <select
                            value={b.material || 'soft_fabric'}
                            onChange={(e) => updateBagField(idx, 'material', e.target.value)}
                            className="w-full border-2 border-black px-2 py-1 text-sm font-mono bg-white"
                          >
                            {['soft_fabric','hard_shell','leather','plastic'].map((m) => (
                              <option key={m} value={m}>{m.replace('_',' ').toUpperCase()}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-mono uppercase text-gray-500">Lock</label>
                          <select
                            value={b.lock_type || 'none'}
                            onChange={(e) => updateBagField(idx, 'lock_type', e.target.value)}
                            className="w-full border-2 border-black px-2 py-1 text-sm font-mono bg-white"
                          >
                            {['none','tsa','combination','key'].map((l) => (
                              <option key={l} value={l}>{l.toUpperCase()}</option>
                            ))}
                          </select>
                        </div>
                        <div className="sm:col-span-1">
                          <label className="text-[10px] font-mono uppercase text-gray-500">Marks</label>
                          <input
                            type="text"
                            value={b.distinguishing_marks || ''}
                            onChange={(e) => updateBagField(idx, 'distinguishing_marks', e.target.value)}
                            className="w-full border-2 border-black px-2 py-1 text-sm font-mono"
                            placeholder="tag color, sticker…"
                          />
                        </div>
                        <BagInsuranceField bag={b} idx={idx} onChange={updateBagField} />
                      </div>
                    </div>
                  ))}
                  {bags.length === 0 && (
                    <div className="text-xs font-mono text-gray-500 italic">No bags on file — you can still attach photos below.</div>
                  )}
                  <button
                    type="button"
                    data-testid="edit-auth-add-bag-btn"
                    onClick={() => setBags((prev) => [...prev, {
                      bag_type: 'suitcase', material: 'soft_fabric', lock_type: 'none', weight: '',
                      color: '', brand: '', dimensions: '', declared_value: 0, insurance_opted: false, images: [],
                    }])}
                    className="w-full py-2 border-2 border-dashed border-black font-mono uppercase text-xs font-bold hover:bg-gray-100"
                  >
                    + Add bag
                  </button>
                  <p className="text-[10px] font-mono text-gray-500">Adding bags or weight changes the price — the customer is asked to pay the extra before pickup.</p>
                </div>
              </div>

              {/* Photo append */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-mono uppercase text-gray-600">
                    ADD PHOTOS ({addPhotos.length} new)
                  </div>
                  {bags.length > 1 && (
                    <div className="flex items-center gap-1 text-[10px] font-mono">
                      <span className="uppercase text-gray-500">ATTACH TO:</span>
                      <select
                        data-testid="edit-auth-photo-target-bag"
                        value={photoTargetBagIdx}
                        onChange={(e) => setPhotoTargetBagIdx(parseInt(e.target.value, 10))}
                        className="border-2 border-black px-1 py-0.5 bg-white font-mono text-[10px]"
                      >
                        {bags.map((_, i) => (
                          <option key={i} value={i}>BAG #{i + 1}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <label className="border-2 border-dashed border-black p-3 flex flex-col items-center gap-1 cursor-pointer hover:bg-gray-50 text-xs font-mono">
                  <Upload size={16} />
                  <span>Click to attach packed-luggage photos</span>
                  <input
                    data-testid="edit-auth-photo-input"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handlePhotoFiles(e.target.files)}
                  />
                </label>
                {addPhotos.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {addPhotos.map((src, i) => (
                      <div key={i} className="relative">
                        <img src={src} alt={`add-${i}`} className="w-full aspect-square object-cover border-2 border-black" />
                        <button
                          type="button"
                          onClick={() => setAddPhotos((p) => p.filter((_, j) => j !== i))}
                          className="absolute -top-2 -right-2 bg-white border-2 border-black rounded-full p-0.5"
                          aria-label="Remove photo"
                        ><X size={12}/></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-mono uppercase text-gray-600">Edit reason (audit trail)</label>
                <input
                  type="text"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="e.g. customer forgot to upload photo of trolley"
                  className="w-full mt-1 border-2 border-black px-3 py-2 text-sm font-mono"
                  data-testid="edit-auth-notes"
                />
              </div>
            </div>
          )}

          {stage === 'done' && (
            <div data-testid="edit-auth-stage-done" className="text-center py-4">
              <div className="text-4xl mb-2">✓</div>
              <p className="font-mono text-sm">
                Booking manifest updated. Any further edits will require a new OTP.
              </p>
              {pricing && pricing.payment_action !== 'none' && (
                <div data-testid="edit-auth-pricing" className="mt-4 p-3 border-2 border-black bg-[#FFF7E6] text-left font-mono text-xs space-y-1">
                  <div>New total: <strong>{formatInr(pricing.new_amount)}</strong> · Already paid: {formatInr(pricing.paid_so_far)}</div>
                  {pricing.payment_action === 'top_up' ? (
                    <div className="font-bold text-red-700" data-testid="edit-auth-topup-msg">
                      Customer must pay extra {formatInr(pricing.top_up_amount)}. They have been sent a WhatsApp and can pay from their dashboard. Pickup is blocked until it is paid.
                    </div>
                  ) : (
                    <div className="font-bold text-green-700" data-testid="edit-auth-refund-msg">
                      Price dropped — refund of {formatInr(pricing.refund_amount)} sent to admin for approval.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {stage === 'editing' && (
          <div className="p-5 pt-3 border-t-2 border-black bg-white flex flex-col sm:flex-row gap-3 sticky bottom-0 rounded-b-[inherit]">
            <button onClick={onClose} className="btn-secondary px-4 py-2 text-sm flex-1">
              CANCEL
            </button>
            <button
              data-testid="edit-auth-save"
              onClick={applySave}
              disabled={busy}
              className="btn-primary px-4 py-2 text-sm flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Save size={14} /> {busy ? 'SAVING…' : 'SAVE & LOCK EDITS'}
            </button>
          </div>
        )}

        {stage === 'done' && (
          <div className="p-5 pt-3 border-t-2 border-black bg-white sticky bottom-0 rounded-b-[inherit]">
            <button onClick={onClose} data-testid="edit-auth-done-close" className="btn-primary w-full py-2 text-sm">DONE</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditAuthorisationModal;
